// Vocabulary of conversations between agents and people. Shared by the server, the MCP tools and the web.
import { z } from "zod";

/**
 * Where a conversation stands. One field, so every surface (web, Discord tags, MCP) agrees:
 * - open            nothing pending on either side
 * - awaiting_human  the agent asked something and waits for the person
 * - awaiting_agent  the person wrote something the agent has not picked up yet
 * - expired         the agent stopped waiting for an answer (its question's expires_at passed)
 * - closed          settled; a new message from either side reopens it
 */
export const THREAD_STATUSES = ["open", "awaiting_human", "awaiting_agent", "expired", "closed"] as const;
export type ThreadStatus = (typeof THREAD_STATUSES)[number];

/** Inbox filters. */
export const INBOX_FILTERS = ["needs_you", "waiting", "done"] as const;
export type InboxFilter = (typeof INBOX_FILTERS)[number];

export const STATUSES_BY_FILTER: Record<InboxFilter, ThreadStatus[]> = {
  needs_you: ["awaiting_human"],
  waiting: ["open", "awaiting_agent"],
  done: ["closed", "expired"],
};

export const MESSAGE_ROLES = ["agent", "human", "system"] as const;
export type MessageRole = (typeof MESSAGE_ROLES)[number];

/** note: plain message; question: the agent waits for an answer; answer: a person picked a choice. */
export const MESSAGE_KINDS = ["note", "question", "answer"] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

/** Where a message was written. */
export const SOURCES = ["mcp", "web", "discord", "system"] as const;
export type Source = (typeof SOURCES)[number];

export const URGENCIES = ["normal", "high"] as const;
export type Urgency = (typeof URGENCIES)[number];

export const Choice = z.object({
  id: z.string().min(1).max(40),
  label: z.string().min(1).max(80),
});
export type Choice = z.infer<typeof Choice>;

export const MAX_CHOICES = 25;
export const MAX_BODY = 8000;
export const MAX_SUBJECT = 120;

/** Product / space keys as Plannello uses them (PLN, CYR, CSC). */
export const ProductKey = z
  .string()
  .trim()
  .regex(/^[A-Za-z][A-Za-z0-9]{1,9}$/, "Use a Plannello product or space key such as PLN")
  .transform((k) => k.toUpperCase());

/** Plannello item keys (PLN-12). */
export const ItemKey = z
  .string()
  .trim()
  .regex(/^[A-Za-z][A-Za-z0-9]{1,9}-\d{1,7}$/, "Use a Plannello item key such as PLN-12")
  .transform((k) => k.toUpperCase());

/** T-12 */
export function threadRef(n: number): string {
  return `T-${n}`;
}

/** "T-12", "t-12", "12" -> 12; null for anything else. */
export function parseThreadRef(value: string): number | null {
  const m = /^(?:t-)?(\d{1,9})$/i.exec(value.trim());
  return m ? Number(m[1]) : null;
}

/**
 * Turns free-form choices ("Option A" or {id,label}) into unique, stable ids: given ids are kept,
 * missing ones become a, b, c... so a person (and the agent) can refer to them briefly.
 */
export function normalizeChoices(input: Array<string | { id?: string; label: string }>): Choice[] {
  const used = new Set<string>();
  const out: Choice[] = [];
  let auto = 0;
  const nextAuto = () => {
    for (;;) {
      const id = auto < 26 ? String.fromCharCode(97 + auto) : `c${auto}`;
      auto++;
      if (!used.has(id)) return id;
    }
  };
  for (const entry of input) {
    const label = (typeof entry === "string" ? entry : entry.label).trim().slice(0, 80);
    if (!label) continue;
    let id = typeof entry === "string" ? "" : (entry.id ?? "").trim().slice(0, 40);
    if (!id || used.has(id)) id = nextAuto();
    used.add(id);
    out.push({ id, label });
  }
  return out;
}
